import { EXERCISES } from '@/data/exercises';

import { GPS_CARDIO_EXERCISE_NAMES } from '@/lib/gpsCardioWorkout';

export interface ExerciseData {
  exercise_name: string;
  sets: number | null;
  reps: number | null;
  weight_kg?: number;
  notes?: string;
  coach_plan_lines?: string[];
  muscle_groups?: string[];
}

export interface SingleWorkoutPlan {
  type: 'single_workout';
  exercises: ExerciseData[];
}

export interface WeeklyWorkoutDay {
  day: string;
  workout_name: string;
  exercises: ExerciseData[];
  is_rest_day?: boolean;
}

/** True when a program exercise is a GPS/outdoor run (not gym sets×reps). */
export const isRunningProgramExercise = (name: string): boolean => {
  const n = String(name || '').toLowerCase().trim();
  if (!n) return false;
  if (GPS_CARDIO_EXERCISE_NAMES.some((g) => n.includes(g.toLowerCase()))) return true;
  return /\b(run|running|jog|tempo|interval|fartlek|long run|easy run|track)\b/.test(n);
};

const coerceInt = (val: unknown): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
  if (typeof val === 'string') {
    const matches = val.match(/-?\d+(?:\.\d+)?/g);
    if (!matches) return undefined;
    const nums = matches.map((m) => parseFloat(m.replace(',', '.'))).filter((x) => !Number.isNaN(x));
    if (!nums.length) return undefined;
    return Math.round(Math.max(...nums));
  }
  return undefined;
};

const coerceFloat = (val: unknown): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const matches = val.replace(',', '.').match(/-?\d+(?:\.\d+)?/g);
    if (!matches) return undefined;
    const nums = matches.map((m) => parseFloat(m)).filter((x) => !Number.isNaN(x));
    if (!nums.length) return undefined;
    return Math.max(...nums);
  }
  return undefined;
};

/** Normalize one program exercise — running days use notes/coach_plan_lines, not sets×reps. */
export const normalizeWeeklyProgramExercise = (raw: Record<string, unknown>): ExerciseData | null => {
  const name = String(raw.exercise_name || raw.name || '').trim();
  if (!name) return null;

  const workoutType = String(raw.workout_type || raw.exercise_type || '').toLowerCase();
  const isRun =
    workoutType === 'running' ||
    raw.is_running === true ||
    isRunningProgramExercise(name);

  const coachLines = Array.isArray(raw.coach_plan_lines)
    ? (raw.coach_plan_lines as unknown[]).map((l) => String(l || '').trim()).filter(Boolean)
    : Array.isArray(raw.coachPlanLines)
      ? (raw.coachPlanLines as unknown[]).map((l) => String(l || '').trim()).filter(Boolean)
      : undefined;

  if (isRun) {
    const notes =
      (coachLines?.length ? coachLines.join('\n') : '') ||
      String(raw.notes || '').trim();
    return {
      exercise_name: name.toLowerCase().includes('running') ? 'Running (Dedicated)' : name,
      sets: null,
      reps: null,
      weight_kg: undefined,
      notes: notes || undefined,
      coach_plan_lines: coachLines,
      muscle_groups: Array.isArray(raw.muscle_groups)
        ? (raw.muscle_groups as string[])
        : ['Cardio', 'Legs'],
    };
  }

  const sets = coerceInt(raw.sets);
  const reps = coerceInt(raw.reps ?? raw.reps_min ?? raw.reps_max);
  if (sets == null || reps == null) return null;

  return {
    exercise_name: name,
    sets,
    reps,
    weight_kg: coerceFloat(raw.weight_kg ?? raw.weight),
    notes: String(raw.notes || '').trim() || undefined,
    muscle_groups: Array.isArray(raw.muscle_groups) ? (raw.muscle_groups as string[]) : undefined,
  };
};

/** Normalize weekly program days for import/review (handles aliases + running days). */
export const normalizeWeeklyProgramForImport = (raw: {
  title?: string;
  description?: string;
  conversationId?: string;
  days?: unknown[];
}): {
  type: 'weekly_plan';
  title: string;
  description: string;
  conversationId?: string;
  days: Array<{
    day: string;
    day_name: string;
    workout: string;
    workout_title: string;
    workout_name: string;
    is_rest_day?: boolean;
    exercises: ExerciseData[];
  }>;
} | null => {
  if (!Array.isArray(raw.days) || raw.days.length === 0) return null;

  const days = raw.days
    .map((dayRaw, idx) => {
      const day = dayRaw as Record<string, unknown>;
      const dayLabel = String(day.day || day.day_name || `Day ${idx + 1}`).trim();
      const workoutTitle = String(
        day.workout_title || day.workout_name || day.workout || dayLabel,
      ).trim();
      const isRest =
        day.is_rest_day === true ||
        (/rest|recovery|off/i.test(workoutTitle) &&
          (!Array.isArray(day.exercises) || (day.exercises as unknown[]).length === 0));

      const exercises = Array.isArray(day.exercises)
        ? (day.exercises as Record<string, unknown>[])
            .map(normalizeWeeklyProgramExercise)
            .filter((ex): ex is ExerciseData => ex != null)
        : [];

      if (!isRest && exercises.length === 0) return null;

      return {
        day: dayLabel,
        day_name: dayLabel,
        workout: workoutTitle,
        workout_title: workoutTitle,
        workout_name: workoutTitle,
        is_rest_day: isRest,
        exercises: isRest ? [] : exercises,
      };
    })
    .filter((d): d is NonNullable<typeof d> => d != null);

  if (days.length === 0) return null;

  return {
    type: 'weekly_plan',
    title: String(raw.title || 'Weekly Workout Program').trim(),
    description: String(raw.description || 'Generated by your AI coach based on your goals').trim(),
    conversationId: raw.conversationId,
    days,
  };
};

export const convertWeeklyPlanToProgramData = (
  plan: WeeklyWorkoutPlan,
  conversationId?: string,
) =>
  normalizeWeeklyProgramForImport({
    title: 'Weekly Workout Program',
    description: 'Generated by your AI coach based on your goals',
    conversationId,
    days: plan.days.map((d) => ({
      day: d.day,
      day_name: d.day,
      workout_title: d.workout_name,
      workout_name: d.workout_name,
      exercises: d.exercises,
    })),
  });

/** Copy coach_plan_lines into notes so import stores the full run, not a title blurb. */
export function persistCoachLinesOnProgramData<T extends {
  days?: Array<{
    exercises?: Array<{
      coach_plan_lines?: string[];
      notes?: string;
    }>;
  }>;
}>(program: T): T {
  return {
    ...program,
    days: (program.days || []).map((day) => ({
      ...day,
      exercises: (day.exercises || []).map((ex) => {
        const lines = (ex.coach_plan_lines || []).map((l) => String(l || '').trim()).filter(Boolean);
        if (!lines.length) return ex;
        return { ...ex, coach_plan_lines: lines, notes: lines.join('\n') };
      }),
    })),
  };
}


export interface WeeklyWorkoutPlan {
  type: 'weekly_plan';
  days: WeeklyWorkoutDay[];
}

export interface RunningInterval {
  repeat?: number;
  distance_km?: number;
  duration_minutes?: number;
  pace?: string;
  rest_minutes?: number;
}

export interface RunningWorkoutPlan {
  type: 'running_workout';
  title?: string;
  warmup_minutes?: number;
  intervals: RunningInterval[];
  cooldown_minutes?: number;
  /** Exact lines for AI Run Guide — Arnold writes these; imported verbatim. */
  coach_plan_lines?: string[];
  notes?: string;
  total_distance_km?: number;
  estimated_duration_minutes?: number;
  pacing_guide?: string[];
}

export interface RunningWorkoutCollection {
  type: 'running_workouts';
  workouts: RunningWorkoutPlan[];
}

export type WorkoutPlan = SingleWorkoutPlan | WeeklyWorkoutPlan | RunningWorkoutPlan | RunningWorkoutCollection;

// Function to find matching exercise from the database
export const findMatchingExercise = (exerciseName: string) => {
  const normalizedName = exerciseName.toLowerCase().trim();
  
  // First try exact match
  let exercise = EXERCISES.find(ex => ex.name.toLowerCase() === normalizedName);
  
  if (exercise) return exercise;
  
  // Try partial match
  exercise = EXERCISES.find(ex => 
    ex.name.toLowerCase().includes(normalizedName) || 
    normalizedName.includes(ex.name.toLowerCase())
  );
  
  if (exercise) return exercise;
  
  // Try alternative names and common variations
  const exerciseMap: { [key: string]: string } = {
    'pushup': 'Push-Up',
    'pushups': 'Push-Up',
    'push up': 'Push-Up',
    'pullup': 'Pull-Up',
    'pullups': 'Pull-Up',
    'pull up': 'Pull-Up',
    'situp': 'Sit-Up',
    'situps': 'Sit-Up',
    'sit up': 'Sit-Up',
    'bicep curl': 'Dumbbell Curl',
    'biceps curl': 'Dumbbell Curl',
    'shoulder press': 'Dumbbell Shoulder Press',
    'military press': 'Overhead Press',
    'chest fly': 'Dumbbell Chest Fly',
    'chest flye': 'Dumbbell Chest Fly',
    'lat pulldown': 'Lat Pulldown With Pronated Grip',
    'barbell row': 'Barbell Row',
    'bent over row': 'Barbell Row',
    'tricep extension': 'Dumbbell Standing Triceps Extension',
    'triceps extension': 'Dumbbell Standing Triceps Extension',
    'leg press': 'Leg Press',
    'calf raise': 'Standing Calf Raise',
    'leg curl': 'Lying Leg Curl',
    'leg extension': 'Leg Extension',
    'hip thrust': 'Hip Thrust',
    'glute bridge': 'Glute Bridge'
  };
  
  const mappedName = exerciseMap[normalizedName];
  if (mappedName) {
    exercise = EXERCISES.find(ex => ex.name === mappedName);
    if (exercise) return exercise;
  }
  
  return null;
};

/** True when Arnold is analyzing a completed session — never treat as a new run prescription. */
export const isWorkoutAnalysisMessage = (message: string): boolean => {
  const text = (message || '').slice(0, 1200).toLowerCase();
  return (
    /\banalyz(e|ing|ed)?\s+my\s+(running|workout|session)\b/.test(text) ||
    /\bworkout\s+analysis\b/.test(text) ||
    /\brunning\s+session\b[\s\S]{0,400}\baverage\s+pace\b/.test(text) ||
    /\bcritical\s+analysis\s+rules\b/.test(text) ||
    /\bjust\s+completed\s+a\s+(strength|running|custom)\s+workout\b/.test(text)
  );
};

/** Educational coaching prose — not a concrete prescription. */
export const isEducationalRunAdvice = (message: string): boolean => {
  const text = (message || '').toLowerCase();
  if (/"type"\s*:\s*"running_workout"/.test(message || '')) return false;
  if (/```json/.test(message || '')) return false;
  const adviceSignals =
    (/\b(here's how|here is how|focusing on key workout types|these runs are crucial|the key is|would you like me to prescribe)\b/i.test(
      text,
    ) ||
      /\b(threshold runs|race pace intervals|long runs with)\s*:/i.test(text)) &&
    !/\bwarm-?up\s*[—–:-]\s*\d+/i.test(text);
  return adviceSignals;
};

/** Fast guard — skip expensive JSON/prose parsing on plain chat messages. */
export const messageLikelyContainsWorkoutPlan = (message: string): boolean => {
  if (!message || message.length < 24) return false;
  if (isWorkoutAnalysisMessage(message)) return false;
  if (isEducationalRunAdvice(message)) return false;
  if (message.includes('```json')) return true;
  if (/"type"\s*:\s*"(single_workout|running_workout|running_workouts|weekly_plan)"/.test(message)) {
    return true;
  }
  // Require concrete structure (warmup/cooldown with numbers OR repeats), not vague "main set" talk.
  const hasStructuredWarmCool =
    /\bwarm-?up\s*[—–:-]?\s*\d+/i.test(message) && /\bcool-?down\s*[—–:-]?\s*\d+/i.test(message);
  const hasRepeats = /\b\d+\s*[x×]\s*\d+/i.test(message);
  const hasCoachLines = /\b(warm-?up|cool-?down|main\s+set)\s*[—–]\s*.+\n.+\n/i.test(message);
  if ((hasStructuredWarmCool || hasRepeats || hasCoachLines) && /\b(min|km|pace|zone\s*[1-5]|bpm)\b/i.test(message)) {
    return true;
  }
  return false;
};

const cleanInferredRunTitle = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  let t = raw.replace(/\*\*/g, '').trim();
  t = t.replace(
    /\s+((?:Excellent|Great|Good|Nice|Solid|Amazing|Perfect|Awesome)\s+(?:question|work|job|point).*)$/i,
    '',
  );
  t = t.replace(/[,!?.:;]+$/g, '').trim();
  if (!t || t.length < 3) return undefined;
  if (/\b(excellent|great)\s+question\b/i.test(t)) return undefined;
  return t.slice(0, 80);
};

/** Reject garbage prose-inferred plans (e.g. 4× identical 10km "Main set"). */
export const isImportableRunningPlan = (plan: RunningWorkoutPlan | null | undefined): boolean => {
  if (!plan || plan.type !== 'running_workout') return false;
  const lines = plan.coach_plan_lines?.map((l) => l.trim()).filter(Boolean) ?? [];
  const intervals = plan.intervals ?? [];
  if (lines.length === 0 && intervals.length === 0 && !plan.warmup_minutes) return false;

  const mains = lines.filter((l) => !/^warm/i.test(l) && !/^cool/i.test(l));
  if (mains.length >= 3) {
    const normalized = mains.map((l) => l.replace(/\s+/g, ' ').toLowerCase());
    const unique = new Set(normalized);
    if (unique.size === 1) return false; // identical repeated mains
  }

  const totalKmFromLines = mains.reduce((sum, l) => {
    const m = l.match(/(\d+(?:\.\d+)?)\s*km/i);
    return sum + (m ? parseFloat(m[1]) : 0);
  }, 0);
  const totalKmFromIntervals = intervals.reduce(
    (sum, it) => sum + (it.distance_km || 0) * (it.repeat && it.repeat > 1 ? it.repeat : 1),
    0,
  );
  const totalKm = Math.max(totalKmFromLines, totalKmFromIntervals);
  if (totalKm > 35 && !mains.some((l) => /\d+\s*[x×]/i.test(l)) && !intervals.some((it) => (it.repeat || 0) > 1)) {
    return false;
  }

  const hasNumericWork =
    mains.some((l) => /\d/.test(l)) ||
    intervals.length > 0 ||
    !!plan.warmup_minutes;
  if (!hasNumericWork) return false;

  if (
    !plan.warmup_minutes &&
    !plan.cooldown_minutes &&
    mains.length > 0 &&
    mains.every((l) => /main set\s*[—–-]\s*10\s*km/i.test(l))
  ) {
    return false;
  }

  return true;
};

/** Build importable running_workout JSON when Arnold describes a run but omits the code block. */
export const inferRunningWorkoutFromProse = (message: string): RunningWorkoutPlan | null => {
  const text = message || '';
  if (isWorkoutAnalysisMessage(text)) return null;
  if (isEducationalRunAdvice(text)) return null;
  const looksLikeRun =
    (/\bwarm-?up\s*[—–:-]?\s*\d+/i.test(text) || /\b\d+\s*[x×]\s*\d+/i.test(text)) &&
    /\b(min|km|pace|zone\s*[1-5]|bpm)\b/i.test(text);
  if (!looksLikeRun) return null;

  const titleMatch = text.match(/^##\s*([^\n]+)/m) || text.match(/##\s*([^\n(]+)/);
  const title = cleanInferredRunTitle(titleMatch?.[1]);

  const warmupM = text.match(/warm-?up[:\s*]*(\d+)\s*min/i);
  const cooldownM = text.match(/cool-?down[:\s*]*(\d+)\s*min/i);

  const intervals: RunningInterval[] = [];
  const mainSection = text.match(/\*\*main\s+set:?\*\*([\s\S]*?)(?=\*\*cool-?down|\*\*pacing|\*\*total|$)/i);
  const block = mainSection?.[1] || '';
  // Never scan the whole educational essay for "10 km at …" — only the Main set block.
  if (!block && !warmupM && !cooldownM) return null;
  const scan = block || text;

  const addUnique = (it: RunningInterval) => {
    const key = JSON.stringify(it);
    if (!intervals.some((x) => JSON.stringify(x) === key)) intervals.push(it);
  };

  const parseRestMinutesFromText = (source: string): number | undefined => {
    const minM = source.match(
      /(?:with|,\s*)?(\d+(?:\.\d+)?)\s*min(?:ute)?s?\s*(?:easy\s+)?(?:recovery|rest)/i,
    );
    if (minM) return parseFloat(minM[1]);
    const secM = source.match(
      /(?:with|,\s*)(\d+(?:\.\d+)?)\s*(?:s|sec(?:onds?)?)\s*(?:easy\s+)?(?:recovery|rest)/i,
    );
    if (secM) return parseFloat(secM[1]) / 60;
    return undefined;
  };

  const restMinutesFromBlock = parseRestMinutesFromText(scan);
  const paceFromScan = (() => {
    const m = scan.match(/(\d{1,2}):(\d{2})\s*\/\s*km/i);
    return m ? `${m[1]}:${m[2]}/km` : undefined;
  })();

  for (const m of scan.matchAll(
    /(\d+)\s*min(?:utes?)?\s+at\s+(?:zone\s*)?Z?([1-5])(?:\s*\([^)]*(\d+)\s*[-–]\s*(\d+)\s*bpm[^)]*\))?/gi,
  )) {
    const dur = parseInt(m[1], 10);
    const zone = `Z${m[2]}`;
    const bpm = m[3] && m[4] ? `${m[3]}-${m[4]} bpm` : undefined;
    addUnique({ duration_minutes: dur, pace: bpm ? `${zone} (${bpm})` : zone });
  }

  for (const m of scan.matchAll(/(\d+)\s*min(?:utes?)?\s+at\s+([^,\n*]+)/gi)) {
    const tail = m[2].trim();
    if (/^z?\d\b/i.test(tail)) continue;
    if (tail.length > 40 || /\b(which|requires|gradually|ability)\b/i.test(tail)) continue;
    addUnique({ duration_minutes: parseInt(m[1], 10), pace: tail.replace(/\*\*/g, '').trim() });
  }

  for (const m of scan.matchAll(/(\d+(?:\.\d+)?)\s*km\s+at\s+([\d:]+\s*\/?\s*km)\b/gi)) {
    addUnique({ distance_km: parseFloat(m[1]), pace: m[2].trim() });
  }

  for (const m of scan.matchAll(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*km/gi)) {
    const lineRest = parseRestMinutesFromText(m[0]) ?? restMinutesFromBlock;
    const zoneM = m[0].match(/(?:zone\s*|z)\s*([1-5])/i);
    addUnique({
      repeat: parseInt(m[1], 10),
      distance_km: parseFloat(m[2]),
      rest_minutes: lineRest,
      pace: zoneM ? `Z${zoneM[1]}` : paceFromScan,
    });
  }

  for (const m of scan.matchAll(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*m(?!in)/gi)) {
    const lineRest = parseRestMinutesFromText(m[0]) ?? restMinutesFromBlock;
    addUnique({
      repeat: parseInt(m[1], 10),
      distance_km: parseFloat(m[2]) / 1000,
      rest_minutes: lineRest,
      pace: paceFromScan,
    });
  }

  const distM = text.match(/(?:total\s+distance|approximately)\s*([\d.]+)\s*(?:[-–]\s*([\d.]+))?\s*km/i);
  const durM = text.match(/estimated\s+duration[:\s*]*(\d+)\s*min/i);

  if (!warmupM && intervals.length === 0 && !cooldownM) return null;

  const coachLines = extractCoachLinesFromArnoldMarkdown(text);
  const plan: RunningWorkoutPlan = {
    type: 'running_workout',
    title: title || undefined,
    warmup_minutes: warmupM ? parseInt(warmupM[1], 10) : undefined,
    intervals,
    cooldown_minutes: cooldownM ? parseInt(cooldownM[1], 10) : undefined,
    coach_plan_lines: coachLines.length ? coachLines : undefined,
    total_distance_km: distM
      ? distM[2]
        ? (parseFloat(distM[1]) + parseFloat(distM[2])) / 2
        : parseFloat(distM[1])
      : undefined,
    estimated_duration_minutes: durM ? parseInt(durM[1], 10) : undefined,
  };

  return isImportableRunningPlan(plan) ? plan : null;
};

// Function to parse JSON from Arnold's response
export const parseWorkoutPlanFromMessage = (message: string): WorkoutPlan | null => {
  try {
    if (!messageLikelyContainsWorkoutPlan(message)) return null;

    // Prefer the LAST JSON code block in the message (most likely the actual plan)
    const blocks = Array.from(message.matchAll(/```json\s*([\s\S]*?)\s*```/g));
    let jsonString = '';

    if (blocks.length > 0) {
      jsonString = blocks[blocks.length - 1][1];
    } else if (/"type"\s*:\s*"(single_workout|running_workout|running_workouts|weekly_plan)"/.test(message)) {
      // Only attempt greedy JSON extraction when the message clearly embeds a plan object.
      const jsonMatch = message.match(/\{[\s\S]*\}/);
      if (jsonMatch && jsonMatch[0].length <= 120_000) {
        jsonString = jsonMatch[0];
      }
    }

    if (!jsonString) return inferRunningWorkoutFromProse(message);

    const parsed = JSON.parse(jsonString);

    // Helpers to coerce numbers safely from mixed formats (e.g., "4", "4x8", "8-10", "80 kg")
    // ALWAYS picks the HIGHEST value from ranges (e.g., "10-12" returns 12, "90-95kg" returns 95)
    const toInt = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.round(Math.max(...nums));
      }
      return undefined;
    };

    const toFloat = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.replace(',', '.').match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n)).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.max(...nums);
      }
      return undefined;
    };

    // Validate the structure
    if (parsed.type === 'single_workout' && Array.isArray(parsed.exercises)) {
      // Normalize and validate exercises
      const normalized = parsed.exercises
        .map((ex: any) => {
          const rawWeight = ex.weight_kg ?? ex.weight ?? ex.weightKg;
          return {
            exercise_name: String(ex.exercise_name || '').trim(),
            sets: toInt(ex.sets),
            reps: toInt(ex.reps),
            weight_kg: rawWeight !== undefined ? toFloat(rawWeight) : undefined,
          };
        })
        .filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number');

      if (normalized.length > 0) {
        return {
          type: 'single_workout',
          exercises: normalized,
        };
      }
    } else if (parsed.type === 'weekly_plan' && Array.isArray(parsed.days)) {
      const normalized = normalizeWeeklyProgramForImport({
        title: parsed.title,
        description: parsed.description,
        days: parsed.days.map((day: Record<string, unknown>) => ({
          ...day,
          exercises: Array.isArray(day.exercises) ? day.exercises : [],
        })),
      });
      if (normalized) {
        return {
          type: 'weekly_plan',
          days: normalized.days.map((d) => ({
            day: d.day,
            workout_name: d.workout_name,
            exercises: d.exercises,
            is_rest_day: d.is_rest_day,
          })),
        };
      }
    } else if (parsed.type === 'running_workout' || parsed.type === 'running_workouts') {
      const toIntSimple = (v: any) => {
        if (typeof v === 'number') return Math.round(v);
        if (typeof v === 'string') { const m = v.match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.round(Math.max(...m.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n)))); }
        return undefined;
      };
      const toFloatSimple = (v: any) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') { const m = v.replace(',', '.').match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.max(...m.map(n => parseFloat(n)).filter(n => !isNaN(n))); }
        return undefined;
      };
      const normalizeRunningWorkout = (raw: any): RunningWorkoutPlan | null => {
        const intervals = Array.isArray(raw.intervals)
          ? raw.intervals.map((it: any) => {
              const restRaw = toFloatSimple(it.rest_minutes);
              return {
                repeat: toIntSimple(it.repeat) || 1,
                distance_km: toFloatSimple(it.distance_km),
                duration_minutes: toFloatSimple(it.duration_minutes),
                pace: typeof it.pace === 'string' ? it.pace : undefined,
                rest_minutes: restRaw != null && restRaw > 0 ? restRaw : undefined,
              };
            })
          : [];
        const coach_plan_lines = Array.isArray(raw.coach_plan_lines)
          ? raw.coach_plan_lines.map((line: any) => String(line || '').trim()).filter(Boolean)
          : undefined;
        const plan: RunningWorkoutPlan = {
          type: 'running_workout',
          title: cleanInferredRunTitle(String(raw.title || '').trim()) || undefined,
          warmup_minutes: toIntSimple(raw.warmup_minutes),
          intervals,
          cooldown_minutes: toIntSimple(raw.cooldown_minutes),
          coach_plan_lines,
          notes: typeof raw.notes === 'string' ? raw.notes : undefined,
          total_distance_km: toFloatSimple(raw.total_distance_km),
          estimated_duration_minutes: toFloatSimple(raw.estimated_duration_minutes),
          pacing_guide: Array.isArray(raw.pacing_guide)
            ? raw.pacing_guide.map((item: any) => String(item || '').trim()).filter(Boolean)
            : undefined,
        };
        if (!(plan.title || intervals.length > 0 || plan.notes || coach_plan_lines?.length)) return null;
        return isImportableRunningPlan(plan) ? plan : null;
      };

      if (parsed.type === 'running_workouts') {
        const workouts = Array.isArray(parsed.workouts)
          ? parsed.workouts.map(normalizeRunningWorkout).filter(Boolean)
          : [];
        if (workouts.length > 0) return { type: 'running_workouts', workouts } as RunningWorkoutCollection;
        return null;
      }

      return normalizeRunningWorkout(parsed);
    }

    return inferRunningWorkoutFromProse(message);
  } catch (error) {
    console.error('Error parsing workout plan from message:', error);
    return inferRunningWorkoutFromProse(message);
  }
};

export type RunningCoachFormatOptions = {
  /** When set, append Z1–Z5 bpm ranges so the live coach can guide by heart rate. */
  hrZones?: Array<{ id: string; lowerBpm: number; upperBpm: number }>;
  healthConnected?: boolean;
};

const zoneBpm = (zones: RunningCoachFormatOptions['hrZones'], id: string): string => {
  const z = zones?.find((x) => x.id === id);
  return z ? `${z.lowerBpm}-${z.upperBpm} bpm` : '';
};

function formatRestForCoach(restMinutes: number, useHr: boolean, z1: string): string {
  const sec = Math.round(restMinutes * 60);
  const label =
    sec >= 60 && sec % 60 === 0
      ? `${sec / 60} minute${sec >= 120 ? 's' : ''}`
      : sec >= 60
        ? `${Math.floor(sec / 60)} minute${Math.floor(sec / 60) !== 1 ? 's' : ''} ${sec % 60} seconds`
        : `${sec} seconds`;
  return ` with ${label} easy recovery${useHr && z1 ? ` at zone 1 (${z1})` : ''}`;
}

/** Split stored program notes into AI Run Guide phase lines. */
export const coachPlanLinesFromNotes = (notes: string | null | undefined): string[] =>
  splitCoachPlanBlob(String(notes || ''));

export function splitCoachPlanBlob(text: string): string[] {
  return String(text || '')
    .split(/\n+| → |; /)
    .map((line) => line.replace(/^\d+\.\s+/, '').replace(/^[-*•]\s+/, '').trim())
    .filter(Boolean)
    .filter((line) =>
      /warm|cool|interval|tempo|main|\d+\s*[x×]|\d+\s*min|\d+(?:\.\d+)?\s*(?:km|miles?|mi\b)/i.test(
        line,
      ),
    );
}

const parseZoneFromText = (text: string): number | null => {
  const m = text.match(/\bzone\s*([1-5])\b/i) || text.match(/\bz([1-5])\b/i);
  return m ? parseInt(m[1] || m[2], 10) : null;
};

const isWarmOrCoolLine = (line: string): boolean =>
  /^(?:warm-?up|cool-?down)\b/i.test(line.trim()) &&
  !/\d+\s*[x×]\s*\d/i.test(line) &&
  !/\bintervals?:/i.test(line);

/** True when coach_plan_lines includes a main-set / interval / distance block (not only warm-up + cool-down). */
export const coachPlanHasMainWork = (lines: string[]): boolean =>
  lines.some((line) => {
    const l = line.trim();
    if (!l) return false;
    if (/\d+\s*[x×]\s*\d/i.test(l)) return true;
    if (/\bintervals?:/i.test(l) && /\d/.test(l)) return true;
    if (isWarmOrCoolLine(l)) return false;
    if (/^main\s+set\b/i.test(l)) return true;
    if (/\d+(?:\.\d+)?\s*(?:km|miles?|mi\b)/i.test(l)) return true;
    if (
      /\d+\s*min/i.test(l) &&
      /\b(tempo|threshold|interval|steady|progressive|easy|moderate|hard|aerobic)\b/i.test(l)
    ) {
      return true;
    }
    return false;
  });

/** Zone for the main set — ignore warmup / recovery Zone 1–2 mentions. */
const parseMainWorkZoneFromText = (text: string): number | null => {
  const stripped = text
    .replace(/warm-?up[\s\S]*?(?=main|interval|\d+\s*[x×]|cool|$)/gi, ' ')
    .replace(/cool-?down[\s\S]*$/gi, ' ')
    .replace(/\b(?:recovery|easy jog)[\s\S]*?(?=interval|\d+\s*[x×]|$)/gi, ' ');
  return parseZoneFromText(stripped);
};

const titleSuggestsQualitySession = (title: string): boolean =>
  /\b(interval|repeats?|vo2|goal\s*pace|threshold|tempo|fartlek|strides?|race\s*pace)\b/i.test(
    title,
  );

const linesAreEasyOnly = (lines: string[]): boolean => {
  const blob = lines.join(' ');
  if (/\d+\s*[x×]\s*\d/i.test(blob)) return false;
  if (/\b(tempo|threshold|interval|z[3-5]|zone\s*[3-5])\b/i.test(blob)) return false;
  return /\b(z2|zone\s*2|easy)\b/i.test(blob);
};

const usableCoachLines = (title: string, lines: string[]): string[] => {
  if (lines.length === 0) return [];
  if (!coachPlanHasMainWork(lines)) return [];
  if (titleSuggestsQualitySession(title) && linesAreEasyOnly(lines)) return [];
  return lines;
};

const parseDurationMinutesFromText = (text: string): number | null => {
  const m = text.match(/(\d+)\s*min(?:ute)?s?/i);
  return m ? parseInt(m[1], 10) : null;
};

const parseDistanceKmFromText = (text: string): { min?: number; max?: number; mid?: number } => {
  const range = text.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*km/i);
  if (range) {
    const min = parseFloat(range[1]);
    const max = parseFloat(range[2]);
    return { min, max, mid: (min + max) / 2 };
  }
  const single = text.match(/(?:approx\.?\s*|~|about\s*|total\s*)?(\d+(?:\.\d+)?)\s*km/i);
  if (single) return { mid: parseFloat(single[1]) };
  return {};
};

const coachLinesLookStructured = (lines: string[]): boolean => {
  if (lines.length < 2) return false;
  const structured = lines.filter(
    (line) =>
      /\d+\s*min/i.test(line) ||
      /\d+\s*km/i.test(line) ||
      /\b(warm|cool|main|interval|tempo|recovery)\b/i.test(line),
  );
  return structured.length >= 2;
};

/**
 * Prefer Arnold's stored phase lines / notes. Only invent a warm-up → main → cool-down
 * template when the program day has no usable plan text at all.
 */
export const buildCoachPlanFromProgramRunDay = (
  day: {
    workout_title: string;
    workout_description?: string | null;
    estimated_duration_minutes?: number | null;
  },
  exercise: { notes?: string | null; coach_plan_lines?: string[] | null },
): string[] => {
  const fromField = usableCoachLines(
    day.workout_title,
    Array.isArray(exercise.coach_plan_lines)
      ? exercise.coach_plan_lines.map((l) => String(l || "").trim()).filter(Boolean)
      : [],
  );
  if (fromField.length > 0) return fromField;

  const coachLines = usableCoachLines(day.workout_title, coachPlanLinesFromNotes(exercise.notes));
  if (coachLines.length > 0) return coachLines;

  const descriptionLines = usableCoachLines(
    day.workout_title,
    coachPlanLinesFromNotes(day.workout_description),
  );
  if (descriptionLines.length > 0) return descriptionLines;

  const combined = [day.workout_title, day.workout_description, exercise.notes]
    .filter(Boolean)
    .join("\n");

  const extracted = usableCoachLines(day.workout_title, extractCoachLinesFromArnoldMarkdown(combined));
  if (extracted.length > 0) return extracted;

  const fromMarkdown = extractProgramDayCoachLinesFromMarkdown(combined, day.workout_title);
  if (fromMarkdown.length > 0) return fromMarkdown;

  if (fromField.length > 0) return fromField;
  if (coachLines.length > 0) return coachLines;
  if (descriptionLines.length > 0) return descriptionLines;

  const duration =
    day.estimated_duration_minutes ??
    parseDurationMinutesFromText(day.workout_title) ??
    parseDurationMinutesFromText(combined) ??
    45;
  const dist = parseDistanceKmFromText(combined);
  const warmupMin = Math.min(10, Math.max(5, Math.round(duration * 0.15)));
  const cooldownMin = Math.min(10, Math.max(5, Math.round(duration * 0.1)));
  const mainMin = Math.max(10, duration - warmupMin - cooldownMin);
  const intervalMatch = combined.match(
    /(\d+)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(miles?|mi\b|km|kilometers?|m|meters?)?/i,
  );
  const looksInterval = /\b(interval|repeats?|vo2|goal\s*pace|[x×]\s*\d)/i.test(combined);
  const looksTempo = /\b(tempo|threshold|comfortably hard)\b/i.test(combined);
  const zone =
    parseMainWorkZoneFromText(combined) ??
    (looksInterval ? 4 : looksTempo ? 3 : 2);

  const zoneHints: Record<number, string> = {
    1: "easy jog (95-114 bpm)",
    2: "conversational pace (120-140 bpm)",
    3: "steady tempo (141-152 bpm)",
    4: "threshold effort (149-167 bpm)",
    5: "hard interval pace (168+ bpm)",
  };

  const lines: string[] = [`${warmupMin} min warm-up at Z1 easy jog (95-114 bpm)`];

  if (intervalMatch) {
    const reps = parseInt(intervalMatch[1], 10);
    const val = parseFloat(intervalMatch[2].replace(",", "."));
    const unit = (intervalMatch[3] || "km").toLowerCase();
    const work =
      unit.startsWith("mile") || unit === "mi"
        ? `${val} mile${val === 1 ? "" : "s"}`
        : unit.startsWith("m") && !unit.startsWith("min")
          ? `${val} m`
          : `${val} km`;
    const restM = combined.match(/(\d+(?:\.\d+)?)\s*min(?:ute)?s?\s*(?:easy\s+)?(?:recovery|rest)/i);
    const rest = restM ? ` with ${restM[1]} minutes easy recovery` : "";
    const paceM = combined.match(/(\d{1,2}:\d{2})\s*\/\s*km/i);
    const pace = paceM ? ` at ${paceM[1]}/km` : ` at Z${zone} ${zoneHints[zone] || ""}`;
    lines.push(`${reps} x ${work}${pace}${rest}`);
  } else {
    let mainLine = `${mainMin} min main set at Z${zone} ${zoneHints[zone] || `zone ${zone}`}`;
    if (dist.mid || dist.min) {
      const kmStr =
        dist.min != null && dist.max != null
          ? `${dist.min}-${dist.max} km`
          : `${Math.round((dist.mid || 0) * 10) / 10} km`;
      mainLine += ` (~${kmStr})`;
    }
    if (day.workout_title?.trim()) {
      mainLine += ` — ${day.workout_title.trim()}`;
    }
    lines.push(mainLine);
  }
  lines.push(`${cooldownMin} min cool-down at Z1 easy jog (95-114 bpm)`);

  return lines;
};

/** Rebuild a RunningWorkoutPlan from a weekly-program run day (notes hold coach_plan_lines). */
export const runningWorkoutPlanFromProgramDay = (
  day: {
    workout_title: string;
    workout_description?: string | null;
    estimated_duration_minutes?: number | null;
  },
  exercise: { notes?: string | null; coach_plan_lines?: string[] | null },
): RunningWorkoutPlan => {
  const coachLines = buildCoachPlanFromProgramRunDay(day, exercise);
  return {
    type: 'running_workout',
    title: day.workout_title,
    coach_plan_lines: coachLines.length ? coachLines : undefined,
    notes: day.workout_description || undefined,
    estimated_duration_minutes: day.estimated_duration_minutes ?? undefined,
    intervals: [],
  };
};

/** Pull runnable phases from Arnold markdown without dropping pace or recovery. */
export function extractCoachLinesFromArnoldMarkdown(text: string): string[] {
  const cleaned = text.replace(/\*\*/g, '').trim();
  if (!cleaned) return [];

  const clip = (raw: string, max = 360) => {
    let s = raw.replace(/\s+/g, ' ').replace(/[.]+$/, '').trim();
    if (s.length > max) s = s.slice(0, max).replace(/\s+\S*$/, '').trim();
    return s;
  };
  const lines: string[] = [];

  const warmMatch = cleaned.match(/(?:^|[\n•*-]\s*)warm-?up\s*[—–:-]\s*([^→\n]+)/i);
  if (warmMatch) {
    const body = clip(warmMatch[1], 180);
    if (body) lines.push(/^warm/i.test(body) ? body : `Warm-up — ${body}`);
  }

  const mainMatch = cleaned.match(
    /(?:^|[\n•*-]\s*)(?:main\s+set|intervals?)\s*[—–:-]\s*([^→\n]*?)(?=(?:^|[\n•*-]|→)\s*(?:cool-?down|pacing\s+guide|total\s+distance|why\s+this|estimated\s+duration)|$)/im,
  );
  if (mainMatch) {
    const body = clip(mainMatch[1], 420);
    const interval =
      body.match(
        /\d+\s*[x×]\s*\d[\s\S]*?(?:recovery|rest|between each)[\s\S]{0,80}/i,
      )?.[0] ||
      body.match(/\d+\s*[x×]\s*\d[^\n]{0,240}/i)?.[0] ||
      body;
    const work = interval.replace(/^main\s+set\s*[—–:-]\s*/i, '').trim();
    if (work) {
      lines.push(/\d+\s*[x×]/i.test(work) || /^\d/.test(work) ? work : `Main set — ${work}`);
    }
  } else {
    const intervalLine = cleaned.match(
      /(\d+\s*[x×]\s*\d+(?:[.,]\d+)?\s*(?:miles?|mi\b|km|m|min|sec)[^\n]{0,240})/i,
    );
    if (intervalLine) lines.push(clip(intervalLine[1], 420));
  }

  const coolMatch = cleaned.match(/(?:^|[\n•*-]\s*)cool-?down\s*[—–:-]\s*([^\n]+)/i);
  if (coolMatch) {
    const body = clip(coolMatch[1], 160);
    if (body) lines.push(/^cool/i.test(body) ? body : `Cool-down — ${body}`);
  }

  return lines.filter(Boolean);
}

/** Recover Arnold's run phases from the weekly-program chat message (Schedule markdown). */
export function extractProgramDayCoachLinesFromMarkdown(
  markdown: string,
  workoutTitle: string,
): string[] {
  const cleaned = String(markdown || '').replace(/\*\*/g, '').trim();
  const title = String(workoutTitle || '').trim();
  if (!cleaned || !title) return [];

  const escaped = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const titleAt = cleaned.search(new RegExp(escaped, 'i'));
  const fromTitle = titleAt >= 0 ? cleaned.slice(titleAt) : cleaned;
  const nextDay = fromTitle.search(/\n-\s+/);
  const dayChunk = nextDay > 0 ? fromTitle.slice(0, nextDay) : fromTitle.split('\n\n')[0] || fromTitle;

  const warmThroughCool = dayChunk.match(/Warm-?up:[^\n]+/i)?.[0];
  const arrowBlob =
    (warmThroughCool && /(?:→|Intervals?:|Cool-?down:)/i.test(warmThroughCool)
      ? warmThroughCool
      : null) ||
    dayChunk.match(/Running[\s\S]{0,80}[—–:—-]\s*([^\n]+)/i)?.[1] ||
    dayChunk.match(/Dedicated\)[\s\S]{0,20}[—–:—-]\s*([^\n]+)/i)?.[1] ||
    dayChunk;

  const fromSplit = splitCoachPlanBlob(arrowBlob);
  if (fromSplit.length >= 2 && coachPlanHasMainWork(fromSplit)) {
    return usableCoachLines(title, fromSplit);
  }

  const merged: string[] = [];
  const seen = new Set<string>();
  for (const line of [...extractCoachLinesFromArnoldMarkdown(arrowBlob), ...fromSplit]) {
    const key = line.toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(line);
  }
  return usableCoachLines(title, merged);
}

function buildCoachPlanLinesFromStructured(
  plan: RunningWorkoutPlan,
  options?: RunningCoachFormatOptions,
): string[] {
  const lines: string[] = [];
  const z2 = zoneBpm(options?.hrZones, 'z2');
  const z3 = zoneBpm(options?.hrZones, 'z3');
  const z4 = zoneBpm(options?.hrZones, 'z4');
  const z1 = zoneBpm(options?.hrZones, 'z1');
  const useHr = !!(options?.healthConnected && options?.hrZones?.length);

  if (plan.warmup_minutes) {
    lines.push(
      useHr && z2
        ? `Warm-up — ${plan.warmup_minutes} minutes easy at zone 2 (${z2})`
        : `Warm-up — ${plan.warmup_minutes} minutes easy`,
    );
  }
  if (plan.intervals?.length) {
    plan.intervals.forEach((interval) => {
      const repeat = interval.repeat && interval.repeat > 1 ? `${interval.repeat} x ` : '';
      const work = interval.distance_km
        ? `${interval.distance_km} km`
        : interval.duration_minutes
          ? `${interval.duration_minutes} minutes`
          : 'run segment';
      const pace = interval.pace ? ` at ${interval.pace}` : '';
      const hrHint =
        useHr && /tempo|threshold|hard|interval|fast/i.test(`${interval.pace || ''}`)
          ? z4
            ? `, target zone 4 (${z4})`
            : z3
              ? `, target zone 3 (${z3})`
              : ''
          : useHr && z2
            ? `, target zone 2 (${z2})`
            : '';
      const rest =
        interval.rest_minutes != null && interval.rest_minutes > 0
          ? formatRestForCoach(interval.rest_minutes, useHr, z1)
          : '';
      const isMainBlock = !!(interval.duration_minutes && !interval.distance_km);
      const prefix = isMainBlock ? 'Main set — ' : '';
      lines.push(`${prefix}${repeat}${work}${pace}${hrHint}${rest}`);
    });
  }
  if (plan.cooldown_minutes) {
    lines.push(
      useHr && z1
        ? `Cool-down — ${plan.cooldown_minutes} minutes easy at zone 1 (${z1})`
        : `Cool-down — ${plan.cooldown_minutes} minutes easy`,
    );
  }
  return lines.filter(Boolean);
}

/** Merge coach_plan_lines, intervals, and Arnold prose into one runnable guide plan. */
export const resolveCoachPlanLines = (
  plan: RunningWorkoutPlan,
  options?: RunningCoachFormatOptions,
): string[] => {
  const existing = plan.coach_plan_lines?.map((l) => l.trim()).filter(Boolean) ?? [];
  if (existing.length > 0 && coachPlanHasMainWork(existing)) return existing;

  const fromStructured = buildCoachPlanLinesFromStructured(plan, options);
  if (coachPlanHasMainWork(fromStructured)) {
    if (existing.length > 0) {
      const warm = existing.find((l) => isWarmOrCoolLine(l) && /^warm/i.test(l));
      const cool = existing.find((l) => isWarmOrCoolLine(l) && /^cool/i.test(l));
      const mains = fromStructured.filter((l) => !isWarmOrCoolLine(l));
      return [warm, ...mains, cool].filter(Boolean) as string[];
    }
    return fromStructured;
  }

  const proseSources = [plan.pacing_guide?.join('\n') ?? '', plan.notes ?? '', plan.title ?? ''].filter(
    Boolean,
  );
  for (const prose of proseSources) {
    const extracted = extractCoachLinesFromArnoldMarkdown(prose);
    if (coachPlanHasMainWork(extracted)) {
      const warm =
        existing.find((l) => /^warm/i.test(l)) ?? extracted.find((l) => /^warm/i.test(l));
      const cool =
        existing.find((l) => /^cool/i.test(l)) ?? extracted.find((l) => /^cool/i.test(l));
      const mains = extracted.filter((l) => !isWarmOrCoolLine(l));
      return [warm, ...mains, cool].filter(Boolean) as string[];
    }
  }

  return existing.length ? existing : fromStructured;
};

export const formatRunningWorkoutForCoach = (
  plan: RunningWorkoutPlan,
  options?: RunningCoachFormatOptions,
): string => {
  return resolveCoachPlanLines(plan, options).join('\n');
};

// Function to convert parsed workout plan to workout logger format
export const convertToWorkoutLoggerFormat = (plan: WorkoutPlan) => {
  if (plan.type === 'single_workout') {
    return plan.exercises.map(exercise => {
      const matchedExercise = findMatchingExercise(exercise.exercise_name);
      return {
        id: `imported-${Date.now()}-${Math.random()}`,
        name: matchedExercise?.name || exercise.exercise_name,
        sets: exercise.sets,
        reps: exercise.reps,
        weight: exercise.weight_kg || 0,
        muscleGroups: matchedExercise?.muscle_groups || [],
        equipment: matchedExercise?.equipment,
        matched: !!matchedExercise
      };
    });
  }
  
  return null; // Weekly plans need different handling
};

// Function to import single workout into workout logger
export const importSingleWorkout = (
  plan: SingleWorkoutPlan,
  onImport: (exercises: any[]) => void
) => {
  const exercises = convertToWorkoutLoggerFormat(plan);
  if (exercises) {
    onImport(exercises);
    return true;
  }
  return false;
};